import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Award,
  AtSign,
  LogOut,
  ChevronRight,
  Check,
  Camera,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/landing/Logo";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { api, useAuth } from "@/context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/* Screen 9 — Profile (approved SalesPit design). */

const EXPERIENCE_OPTIONS = ["0-1 Years", "1-2 Years", "2-5 Years", "5+ Years"];

const USERNAME_RE = /^[a-z0-9_]{3,20}$/;
const USERNAME_COOLDOWN_DAYS = 30;

const Row = ({ icon: Icon, label, value, onClick, testid, danger }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`flex w-full items-center gap-4 rounded-2xl border border-white/10 bg-[#141414] px-5 py-4 text-left transition-colors duration-200 hover:border-white/20 ${
      danger ? "hover:border-[#E52525]/40" : ""
    }`}
  >
    <span
      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
        danger ? "bg-[#E52525]/10 text-[#E52525]" : "bg-white/5 text-zinc-300"
      }`}
    >
      <Icon size={18} />
    </span>
    <span className={`flex-1 font-semibold ${danger ? "text-[#E52525]" : "text-white"}`}>
      {label}
    </span>
    {value && <span className="max-w-[55%] truncate text-sm text-zinc-400">{value}</span>}
    {!danger && <ChevronRight size={18} className="text-zinc-600" />}
  </button>
);

/* Compute how many days remain in the 30-day username-change cooldown, or 0 if none. */
const cooldownDaysLeft = (isoAt) => {
  if (!isoAt) return 0;
  const changed = new Date(isoAt).getTime();
  if (Number.isNaN(changed)) return 0;
  const elapsedDays = Math.floor((Date.now() - changed) / (1000 * 60 * 60 * 24));
  return Math.max(0, USERNAME_COOLDOWN_DAYS - elapsedDays);
};

export default function Profile() {
  const navigate = useNavigate();
  const { user, setUser, logout } = useAuth();
  const [saving, setSaving] = useState(false);

  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [removing, setRemoving] = useState(false);

  const [usernameOpen, setUsernameOpen] = useState(false);
  const [draftUsername, setDraftUsername] = useState("");
  const [usernameError, setUsernameError] = useState(null);
  const [savingUsername, setSavingUsername] = useState(false);

  const initial = (user?.name || "U").charAt(0).toUpperCase();
  const avatar = user?.picture; // backend serializer returns avatar_url || picture
  const hasUploadedAvatar = !!user?.avatar_url;
  const experience = user?.experience_level || "Not set";
  const username = user?.username || "";
  const daysLeft = cooldownDaysLeft(user?.username_updated_at);

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const setExperience = async (value) => {
    setSaving(true);
    try {
      const res = await api.post("/auth/experience", {
        experience_level: value,
      });
      setUser(res.data);
      toast.success("Experience level updated.");
    } catch {
      toast.error("Could not update. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const onAvatarClick = () => {
    if (uploading) return;
    fileInputRef.current?.click();
  };

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file later
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp|gif)$/i.test(file.type)) {
      toast.error("Please pick a PNG, JPG, WEBP, or GIF.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Avatar must be 2 MB or smaller.");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/auth/avatar", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUser(res.data);
      toast.success("Profile picture updated.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not upload avatar.";
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const confirmRemoveAvatar = async () => {
    if (removing) return;
    setRemoving(true);
    try {
      const res = await api.delete("/auth/avatar");
      setUser(res.data);
      toast.success("Profile picture removed.");
      setRemoveOpen(false);
    } catch {
      toast.error("Could not remove profile picture. Please try again.");
    } finally {
      setRemoving(false);
    }
  };

  const openUsernameDialog = () => {
    if (daysLeft > 0) {
      toast.info(`You can change your username again in ${daysLeft} day(s).`);
      return;
    }
    setDraftUsername(username);
    setUsernameError(null);
    setUsernameOpen(true);
  };

  const submitUsername = async () => {
    const candidate = draftUsername.trim().toLowerCase().replace(/^@/, "");
    if (!USERNAME_RE.test(candidate)) {
      setUsernameError(
        "3-20 chars, lowercase letters, numbers, or underscore only."
      );
      return;
    }
    if (candidate === username) {
      setUsernameOpen(false);
      return;
    }
    setSavingUsername(true);
    setUsernameError(null);
    try {
      const res = await api.patch("/auth/username", { username: candidate });
      setUser(res.data);
      toast.success("Username updated.");
      setUsernameOpen(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setUsernameError(detail || "Could not update username.");
    } finally {
      setSavingUsername(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-[#0A0A0A] font-body text-white">
      <DashboardSidebar active="profile" />

      <main className="flex-1 px-6 py-7 md:px-12 md:py-10" data-testid="profile-page">
        {/* mobile logo bar */}
        <div className="mb-8 flex items-center justify-between md:hidden">
          <Logo className="text-2xl" />
        </div>

        <div className="mx-auto max-w-2xl">
          {/* Identity */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center gap-5"
          >
            <button
              type="button"
              onClick={onAvatarClick}
              disabled={uploading}
              data-testid="avatar-upload-trigger"
              className="group relative outline-none"
              aria-label="Change profile picture"
            >
              {avatar ? (
                <img
                  src={avatar}
                  alt={user?.name || "avatar"}
                  className="h-20 w-20 rounded-full object-cover"
                />
              ) : (
                <span
                  data-testid="avatar-fallback"
                  className="flex h-20 w-20 items-center justify-center rounded-full bg-[#E52525] text-2xl font-bold text-white"
                >
                  {initial}
                </span>
              )}
              <span className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full border-2 border-[#0A0A0A] bg-[#141414] text-zinc-300 transition-colors group-hover:text-white">
                {uploading ? (
                  <span className="h-3 w-3 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                ) : (
                  <Camera size={12} />
                )}
              </span>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                className="hidden"
                onChange={onFilePicked}
                data-testid="avatar-file-input"
              />
            </button>
            <div className="min-w-0 flex-1">
              <h1
                data-testid="profile-username"
                className="font-heading truncate text-2xl font-bold text-white"
              >
                {username ? username : "Set username"}
              </h1>
              <p data-testid="profile-email" className="mt-0.5 truncate text-sm text-zinc-500">
                {user?.email}
              </p>
              {hasUploadedAvatar && (
                <button
                  type="button"
                  data-testid="remove-avatar-btn"
                  onClick={() => setRemoveOpen(true)}
                  disabled={uploading || removing}
                  className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-zinc-500 transition-colors hover:text-[#E52525] disabled:opacity-40"
                >
                  <Trash2 size={12} />
                  Remove photo
                </button>
              )}
            </div>
          </motion.div>

          {/* Details */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.08 }}
            className="mt-10 space-y-3"
          >
            <Row
              icon={AtSign}
              label="Username"
              value={username ? username : "Set username"}
              testid="profile-username-row"
              onClick={openUsernameDialog}
            />

            {/* Experience level with dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="profile-experience"
                  disabled={saving}
                  className="flex w-full items-center gap-4 rounded-2xl border border-white/10 bg-[#141414] px-5 py-4 text-left transition-colors duration-200 hover:border-white/20 disabled:opacity-60"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/5 text-zinc-300">
                    <Award size={18} />
                  </span>
                  <span className="flex-1 font-semibold text-white">Experience Level</span>
                  <span className="text-sm text-zinc-400">{experience}</span>
                  <ChevronRight size={18} className="text-zinc-600" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-48 border-white/10 bg-[#141414] text-white"
              >
                {EXPERIENCE_OPTIONS.map((opt) => (
                  <DropdownMenuItem
                    key={opt}
                    data-testid={`experience-option-${opt.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                    onClick={() => setExperience(opt)}
                    className="cursor-pointer justify-between focus:bg-white/10 focus:text-white"
                  >
                    {opt}
                    {experience === opt && <Check size={15} className="text-[#E52525]" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </motion.div>

          {/* Sign out */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.16 }}
            className="mt-8"
          >
            <Row icon={LogOut} label="Sign Out" testid="sign-out-btn" danger onClick={handleLogout} />
          </motion.div>
        </div>
      </main>

      {/* Username edit dialog */}
      <Dialog
        open={usernameOpen}
        onOpenChange={(open) => !savingUsername && setUsernameOpen(open)}
      >
        <DialogContent
          data-testid="username-dialog"
          className="border-white/10 bg-[#141414] text-white"
        >
          <DialogHeader>
            <DialogTitle className="text-white">Choose a username</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Your username is your unique @handle. You can change it once every {USERNAME_COOLDOWN_DAYS} days.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2">
            <label className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
              Username
            </label>
            <div className="mt-1.5 flex items-center rounded-xl border border-white/10 bg-black/40 px-3 focus-within:border-[#E52525]/50">
              <span className="text-sm text-zinc-500">@</span>
              <input
                data-testid="username-input"
                autoFocus
                value={draftUsername}
                onChange={(e) => {
                  setDraftUsername(e.target.value.toLowerCase());
                  if (usernameError) setUsernameError(null);
                }}
                placeholder="mordecai"
                maxLength={20}
                className="w-full bg-transparent py-2.5 pl-1 text-sm text-white placeholder:text-zinc-600 focus:outline-none"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !savingUsername) submitUsername();
                }}
              />
            </div>
            {usernameError ? (
              <p
                data-testid="username-error"
                className="mt-2 text-xs text-[#E52525]"
              >
                {usernameError}
              </p>
            ) : (
              <p className="mt-2 text-xs text-zinc-500">
                3-20 characters. Lowercase letters, numbers, and underscore.
              </p>
            )}
          </div>

          <DialogFooter>
            <button
              type="button"
              disabled={savingUsername}
              onClick={() => setUsernameOpen(false)}
              className="rounded-full border border-white/10 px-4 py-2 text-sm font-semibold text-zinc-200 transition-colors hover:bg-white/5 hover:text-white disabled:opacity-50"
              data-testid="username-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={savingUsername}
              onClick={submitUsername}
              className="rounded-full bg-[#E52525] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#c81f1f] disabled:opacity-50"
              data-testid="username-save"
            >
              {savingUsername ? "Saving…" : "Save"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove-photo confirmation */}
      <AlertDialog
        open={removeOpen}
        onOpenChange={(open) => !removing && setRemoveOpen(open)}
      >
        <AlertDialogContent
          data-testid="remove-avatar-dialog"
          className="border-white/10 bg-[#141414] text-white"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              Remove profile picture?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Your uploaded photo will be deleted and you'll revert to the default avatar. You can upload a new one any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="remove-avatar-cancel"
              disabled={removing}
              className="border-white/10 bg-transparent text-zinc-200 hover:bg-white/5 hover:text-white"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="remove-avatar-confirm"
              onClick={(e) => {
                e.preventDefault();
                confirmRemoveAvatar();
              }}
              disabled={removing}
              className="bg-[#E52525] text-white hover:bg-[#c81f1f]"
            >
              {removing ? "Removing…" : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
